/**
 * @file AntiBodyAntiHBsListView.jsx
 * @author Daniel Aguirre
 * @description Dashboard Detail Management View Page
 */

// Dependencies
import { useTranslation } from 'react-i18next';
import React from 'react';
import PropTypes from 'prop-types';
import { FileSearchOutlined } from '@ant-design/icons';

// Helpers
import { setStorageItem } from '@nephro/commons/helpers/storage-helper';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router';
import {
  joinArrayObject,
  removeNullValuesFromObject,
  sortStringPlus
} from 'commons/helpers/utils';
import {
  createColumns,
  getSearchApiKeys,
  getSelectFilter,
  getSorterString,
  PAGE_LIMIT
} from '../helper/utils';
import DashboardDetailList from '../dashboardDetailList';

function AmbulatoryMonitoringIGView({
  dashboardStaffActions,
  clinicId,
  patientActions
}) {
  const { t: translate } = useTranslation();
  const [rowData, setRowData] = React.useState([]);
  const { classification, totalItems: totalItemsParams } = useParams();
  const [totalItems, setTotalItems] = React.useState(totalItemsParams);
  const [dataFilter, setDataFilter] = React.useState(null);
  const [columns, setColumns] = React.useState([]);
  const [filter, setFilter] = React.useState(null);
  const [sorter, setSorter] = React.useState(null);
  const history = useHistory();

  const { getIndiGlucoseMonitoringListPlus } = dashboardStaffActions;
  const { getFilterPatients } = patientActions;

  const onGetRowData = (_, response) => {
    if (response?.data) {
      setStorageItem('patientInfo', {
        id_patient: response.data.content[0].id_patient,
        first_name: response.data.content[0].first_name,
        last_name: response.data.content[0].last_name,
        id_document: response.data.content[0].id_document
      });
      history.push(`/main/patient-blood-presure`);
    }
  };

  const onGetPatients = (page = 0, filterWord = ' ') => {
    getFilterPatients(
      { paged: true, page, limit: 10, filter: filterWord, clinicId },
      onGetRowData
    );
  };
  const handleDetailClick = async (item) => {
    setStorageItem('adminInfo', {
      diagnostico_diabetes: item.diagnostico_diabetes,
      diagnostico_hipertension: item.diagnostico_hipertension
    });
    onGetPatients(0, item.documento);
  };

  const columnViewRegister = {
    title: '',
    dataIndex: 'documento',
    ellipsis: true,
    // eslint-disable-next-line react/display-name
    render: (_, item) => {
      return (
        <div>
          <button onClick={() => handleDetailClick(item)}>
            <a>
              <div className="flex flex-auto justify-center items-center">
                <FileSearchOutlined />
                Ver registros
              </div>
            </a>
          </button>
        </div>
      );
    }
  };

  const additionalColumns = [
    {
      title: translate('dashboardStaff.diagnosis_diabetes'),
      dataIndex: 'diagnostico_diabetes',
      filter: {
        listName: 'listaDiagnosticoDiabetes',
        searchName: 'filtroDiagnosticoDiabetes',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('diagnostico_diabetes'),
        multiple: 16
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'listaDiagnosticoDiabetes',
            searchName: 'filtroDiagnosticoDiabetes'
          },
          dataIndex: 'diagnostico_diabetes'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.diagnosis_hypertensions'),
      dataIndex: 'diagnostico_hipertension',
      filter: {
        listName: 'listaDiagnosticoHipertension',
        searchName: 'filtroDiagnosticoHipertension',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('diagnostico_hipertension'),
        multiple: 16
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'listaDiagnosticoHipertension',
            searchName: 'filtroDiagnosticoHipertension'
          },
          dataIndex: 'diagnostico_hipertension'
        },
        dataFilter
      ),
      ellipsis: true
    }
  ];

  const getetData = (offset, pageLimit) => {
    const pageSize = pageLimit || PAGE_LIMIT;
    const joinFilter = joinArrayObject(filter);
    const joinSorter = joinArrayObject(sorter);
    const data = {
      params: {
        filtroVariacionSeleccionada: classification,
        idClinic: clinicId,
        limit: pageSize,
        offset,
        ...joinFilter,
        ...joinSorter
      }
    };
    getIndiGlucoseMonitoringListPlus(data, (_, resp) => {
      if (resp?.data && resp.data?.totalRegistros != null) {
        const formatData = resp?.data?.listaDetalles?.map((row) => ({
          ...row,
          tipodoc: row.tipodoc
        }));
        setTotalItems(resp.data?.totalRegistros);
        setRowData(formatData ?? []);
        setDataFilter(resp.data);
      }
    });
  };

  React.useEffect(() => {
    if (clinicId !== null) getetData(1);
  }, [clinicId]);

  React.useEffect(() => {
    const cols = createColumns(translate, dataFilter);
    setColumns(cols);
  }, [rowData, dataFilter]);

  const handleTableChange = (pagination, filters, sorterFilter) => {
    if (filters || sorterFilter) {
      const convertedFilters = getSearchApiKeys(
        [...columns, ...additionalColumns],
        removeNullValuesFromObject(filters)
      );
      const convertedSorts = getSorterString(sorterFilter);
      setFilter(convertedFilters);
      setSorter(convertedSorts);
    }
  };

  return (
    <DashboardDetailList
      columns={[columnViewRegister, ...columns]}
      rowData={rowData}
      totalItems={totalItems}
      onPaginationChange={getetData}
      additionalColumns={additionalColumns}
      onChange={handleTableChange}
      filter={filter}
      sorter={sorter}
    />
  );
}

/**
 * Component definition of proptypes.
 */
AmbulatoryMonitoringIGView.propTypes = {
  dashboardStaffActions: PropTypes.shape({
    getIndiGlucoseMonitoringListPlus: PropTypes.func.isRequired
  }),
  clinicId: PropTypes.any,
  patientActions: PropTypes.shape({
    getFilterPatients: PropTypes.func.isRequired
  }).isRequired
};

export default AmbulatoryMonitoringIGView;
